import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  Notification,
  NotificationFilterType,
  NotificationPreference,
  NotificationResponse,
  UpdateIsReadFlagRequest,
  UpdateThresholdDaysRequest,
} from '../models/notification.model';
import { tap } from 'rxjs/operators';
import { ApiResponse } from '../models/api-response.models';
import { AppService } from './app.service';
import { environment } from '../../../environments/environment';

enum DataAuditType {
  Created = 86,
  Updated = 87,
  Deleted = 88,
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly apiUrl = environment.apiUrl;
  private readonly STORAGE_KEY = 'unread_notification_count';

  private readonly allNotificationsSubject = new BehaviorSubject<
    Notification[]
  >([]);
  private readonly unreadNotificationsSubject = new BehaviorSubject<
    Notification[]
  >([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(
    this.getSavedUnreadCount()
  );
  private readonly notificationSettings = new BehaviorSubject<Notification[]>(
    []
  );
  private readonly selectedOrgIdSubject = new BehaviorSubject<number | null>(
    null
  );
  private currentOrganizationId: number | null = null;

  allNotifications$ = this.allNotificationsSubject.asObservable();
  unreadNotifications$ = this.unreadNotificationsSubject.asObservable();
  unreadCount$ = this.unreadCountSubject.asObservable();
  notificationSettings$ = this.notificationSettings.asObservable();
  selectedOrgId$ = this.selectedOrgIdSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly appService: AppService
  ) {
    this.unreadCountSubject.next(this.getSavedUnreadCount());
    this.appService.notificationReceived$.subscribe(notification => {
      try {
        const notificationData = JSON.parse(notification.body);
        if (notificationData.BroadcastTypeId === 105) {
          return;
        }

        if (notificationData.BroadcastTypeId === 104) {
          if (notificationData.DataAuditType === DataAuditType.Created) {
            this.persistAndUpdateCount(this.unreadCountSubject.value + 1);
          } else if (notificationData.DataAuditType === DataAuditType.Deleted) {
            this.persistAndUpdateCount(this.unreadCountSubject.value - 1);
          }
        } else {
          this.persistAndUpdateCount(this.unreadCountSubject.value + 1);
        }
      } catch (error) {
        console.error('Error parsing notification data:', error);
        this.persistAndUpdateCount(this.unreadCountSubject.value + 1);
      }
    });
  }

  setSelectedOrgId(id: number | null): void {
    this.selectedOrgIdSubject.next(id);
    this.currentOrganizationId = id;
  }

  private getSavedUnreadCount(): number {
    try {
      const value = Number.parseInt(
        localStorage.getItem(this.STORAGE_KEY) || '0',
        10
      );
      return Number.isNaN(value) ? 0 : Math.max(0, value);
    } catch {
      return 0;
    }
  }

  private persistAndUpdateCount(count: number): void {
    const validCount = Math.max(0, count);
    localStorage.setItem(this.STORAGE_KEY, validCount.toString());
    this.unreadCountSubject.next(validCount);
  }

  private buildUrl(
    endpoint: string,
    params: Record<string, unknown> = {}
  ): string {
    const url = `${this.apiUrl}/${endpoint}`;
    const queryParams = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    return queryParams ? `${url}?${queryParams}` : url;
  }

  getNotifications(orgId?: number): Observable<NotificationResponse> {
    let url: string;
    if (orgId !== undefined && orgId !== null) {
      url = this.buildUrl('systemnotification/list', { organizationId: orgId });
    } else {
      url = this.buildUrl('systemnotification/list');
    }
    return this.http.get<NotificationResponse>(url).pipe(
      tap(response => {
        if (response.result) {
          this.notificationSettings.next(response.result);
        }
      })
    );
  }

  getNotificationsByUserId(
    filterType: NotificationFilterType = NotificationFilterType.All,
    orgID: number | null
  ): Observable<NotificationResponse> {
    const params: Record<string, unknown> = { filterType };
    if (orgID && orgID > 0) {
      params['organizationId'] = orgID;
    }

    return this.http
      .get<NotificationResponse>(
        this.buildUrl('systemnotification/get-notification-by-user-id', params)
      )
      .pipe(
        tap(response => {
          if (response.result?.length) {
            this.combineWithBroadcastNotifications(response.result);
          }
        })
      );
  }

  updateIsReadFlag(
    request: UpdateIsReadFlagRequest
  ): Observable<ApiResponse<boolean>> {
    return this.http
      .post<
        ApiResponse<boolean>
      >(`${this.apiUrl}/systemnotification/update-isread-flag`, request)
      .pipe(
        tap(() => {
          const allNotifications = this.allNotificationsSubject.value;
          const targetNotification = allNotifications.find(
            n =>
              n.id === request.notificationId &&
              n.isBroadcast === request.isBroadcast
          );
          const wasRead = targetNotification?.isRead ?? true;

          if (wasRead === request.isRead) return;

          const updatedAllNotifications = allNotifications.map(notification =>
            notification.id === request.notificationId &&
            notification.isBroadcast === request.isBroadcast
              ? { ...notification, isRead: request.isRead }
              : notification
          );

          this.allNotificationsSubject.next(updatedAllNotifications);

          if (request.isRead) {
            this.unreadNotificationsSubject.next(
              this.unreadNotificationsSubject.value.filter(
                notification =>
                  !(
                    notification.id === request.notificationId &&
                    notification.isBroadcast === request.isBroadcast
                  )
              )
            );
            this.persistAndUpdateCount(this.unreadCountSubject.value - 1);
          } else {
            const notificationToAdd = updatedAllNotifications.find(
              n =>
                n.id === request.notificationId &&
                n.isBroadcast === request.isBroadcast
            );
            if (
              notificationToAdd &&
              !this.unreadNotificationsSubject.value.some(
                n =>
                  n.id === request.notificationId &&
                  n.isBroadcast === request.isBroadcast
              )
            ) {
              this.unreadNotificationsSubject.next([
                notificationToAdd,
                ...this.unreadNotificationsSubject.value,
              ]);
              this.persistAndUpdateCount(this.unreadCountSubject.value + 1);
            }
          }
        })
      );
  }

  clearNotifications(): void {
    this.allNotificationsSubject.next([]);
    this.unreadNotificationsSubject.next([]);
    this.persistAndUpdateCount(0);
  }

  createNotificationPreference(
    preference: NotificationPreference
  ): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/systemnotification/create-notification-preference`,
      preference
    );
  }

  updateThresholdDays(
    request: UpdateThresholdDaysRequest
  ): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/systemnotification/update-threshold-days`,
      request
    );
  }

  combineWithBroadcastNotifications(
    broadcastNotifications: Notification[]
  ): void {
    if (!broadcastNotifications?.length) return;

    const currentNotifications = this.allNotificationsSubject.value;

    const existingNotificationsMap = new Map(
      currentNotifications.map(n => [`${n.id}-${n.isBroadcast}`, n])
    );

    const formattedBroadcastNotifications = broadcastNotifications
      .filter(
        broadcast =>
          !existingNotificationsMap.has(
            `${broadcast.id}-${broadcast.isBroadcast}`
          )
      )
      .map(broadcast => ({
        ...broadcast,
        type: 'broadcast',
      }));

    if (!formattedBroadcastNotifications.length) return;

    const allNotifications = [
      ...currentNotifications,
      ...formattedBroadcastNotifications,
    ];
    this.allNotificationsSubject.next(allNotifications);
    this.setAllNotifications(allNotifications);
    const unreadNotifications = allNotifications.filter(n => !n.isRead);
    this.unreadNotificationsSubject.next(unreadNotifications);
    this.persistAndUpdateCount(unreadNotifications.length);
  }

  private setAllNotifications(notifications: Notification[]): void {
    const sorted = [...notifications].sort((a, b) => {
      const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
      return bTime - aTime;
    });

    this.allNotificationsSubject.next(sorted);
    const unread = sorted.filter(n => !n.isRead);
    this.unreadNotificationsSubject.next(unread);
    this.persistAndUpdateCount(unread.length);
  }
}
